import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, MessageSquareWarning } from 'lucide-react';
import api from '../services/api';
import { LOW_RATING_MAX } from '../utils/noteRatings';

interface NoteRatingModalProps {
  noteId: string;
  noteTitle: string;
  onClose: () => void;
  onSubmitted?: () => void;
}

const NoteRatingModal: React.FC<NoteRatingModalProps> = ({
  noteId,
  noteTitle,
  onClose,
  onSubmitted,
}) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const needsFeedback = rating > 0 && rating <= LOW_RATING_MAX;

  const handleSubmit = async () => {
    if (rating < 1) {
      setError('Lütfen 1-5 arası puan verin');
      return;
    }
    if (needsFeedback && feedback.trim().length < 10) {
      setError('Düşük puan için en az 10 karakterlik geri bildirim zorunludur');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await api.rateNote(noteId, rating, needsFeedback ? feedback.trim() : feedback.trim() || undefined);
      onSubmitted?.();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Puan kaydedilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.88)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2100,
          padding: 20,
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.92, y: 12 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.92 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: 440,
            background: 'linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.98))',
            borderRadius: 20,
            padding: '28px 24px',
            border: '1px solid rgba(251,191,36,0.25)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '1.25rem' }}>Notu Puanla</h3>
              <p style={{ margin: '8px 0 0', color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>
                İndirdiğiniz notu değerlendirin
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Kapat"
              style={{
                background: 'transparent',
                border: 'none',
                color: '#64748b',
                cursor: 'pointer',
                padding: 4,
              }}
            >
              <X size={20} />
            </button>
          </div>

          <p
            style={{
              color: '#cbd5e1',
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 16,
              wordBreak: 'break-word',
            }}
          >
            {noteTitle}
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: needsFeedback ? 16 : 24 }}>
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onMouseEnter={() => setHover(value)}
                onMouseLeave={() => setHover(0)}
                onClick={() => {
                  setRating(value);
                  setError('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                }}
              >
                <Star
                  size={34}
                  color="#fbbf24"
                  fill={(hover || rating) >= value ? '#fbbf24' : 'none'}
                />
              </button>
            ))}
          </div>

          {needsFeedback && (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: '#fbbf24',
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                <MessageSquareWarning size={16} />
                Düşük puan — nedenini kısaca yazın (zorunlu)
              </div>
              <textarea
                value={feedback}
                onChange={(e) => {
                  setFeedback(e.target.value);
                  setError('');
                }}
                placeholder="Örn: Not güncel değil, konu eksik, okunaksız..."
                rows={4}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: 12,
                  borderRadius: 12,
                  border: '1px solid rgba(251,191,36,0.3)',
                  background: 'rgba(15,23,42,0.7)',
                  color: '#e2e8f0',
                  fontSize: 14,
                  resize: 'vertical',
                  outline: 'none',
                }}
              />
            </div>
          )}

          {error && (
            <p style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: 12,
                border: '1px solid rgba(71,85,105,0.5)',
                background: 'transparent',
                color: '#94a3b8',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Atla
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || rating < 1}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                color: '#0f172a',
                fontWeight: 700,
                cursor: submitting || rating < 1 ? 'not-allowed' : 'pointer',
                opacity: submitting || rating < 1 ? 0.6 : 1,
              }}
            >
              {submitting ? 'Kaydediliyor...' : 'Gönder'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NoteRatingModal;
